/**
 * Ход работы агента (agroai-6al.17), как в Claude Code: вместо раскрытых
 * карточек инструментов — одна строка «Сейчас: …» с таймером, пока агент
 * работает, и свёрнутый блок «Ход работы · N шагов · 42 с», который можно
 * раскрыть. Шаги — человеческим языком (`steps.ts`), без SQL и внутренних имён.
 */

import type { DynamicToolUIPart } from 'ai';
import { LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Disclosure } from '../astra/ui';
import { Icon } from '../astra/ui/Icon';
import { doneText, nowText, seconds, stepsWord } from './steps';
import { defineMessages } from '../i18n/define';
import { useT } from '../i18n/hooks';

const m = defineMessages({
  writing: { id: 'ai.worklog.writing', defaultMessage: 'пишу ответ', description: 'Что агент делает сейчас в строке «Сейчас: …», от первого лица, строчными: пишет ответ' },
  reviewing: { id: 'ai.worklog.reviewing', defaultMessage: 'обдумываю результат', description: 'Что агент делает сейчас в строке «Сейчас: …», от первого лица, строчными: обдумывает полученные данные' },
  thinking: { id: 'ai.worklog.thinking', defaultMessage: 'думаю над вопросом', description: 'Что агент делает сейчас в строке «Сейчас: …», от первого лица, строчными: размышляет над вопросом' },
  now: { id: 'ai.worklog.now', defaultMessage: 'Сейчас: {doing}…', description: 'Строка статуса идущего ответа ИИ (все роли); {doing} — что агент делает, строчными, например «считаю данные»' },
  summaryLabel: { id: 'ai.worklog.label', defaultMessage: 'Ход работы', description: 'Заголовок раскрываемого блока со списком шагов агента; рядом число шагов и время' },
});

export interface WorkStep {
  part: DynamicToolUIPart;
  /** Сколько шёл шаг, мс; `null` — неизвестно или ещё идёт. */
  ms: number | null;
}

/** Текущее время, обновляемое раз в секунду, пока `active`. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

const pending = (part: DynamicToolUIPart) => part.state === 'input-streaming' || part.state === 'input-available';

/** Строка статуса идущего ответа: «Сейчас: ищу данные… · 12 с». */
export function StatusLine({
  steps,
  writing,
  startedAt,
  thought = null,
}: {
  steps: WorkStep[];
  writing: boolean;
  startedAt: number;
  /** Что модель обдумывает (6al.62, Г9): фраза из её рассуждения, пока ответ не пишется. */
  thought?: string | null;
}) {
  const t = useT();
  const now = useNow(true);
  const current = [...steps].reverse().find((step) => pending(step.part));
  const doing = current ? nowText(current.part) : writing ? t(m.writing) : steps.length ? t(m.reviewing) : t(m.thinking);
  const showThought = Boolean(thought) && !writing && !current;
  return (
    <>
      <p className="agent-status" role="status">
        <Icon icon={LoaderCircle} size={16} className="agent-status__spin" />
        <span>{t(m.now, { doing })}</span>
        <span className="agent-status__time">{seconds(Math.max(0, now - startedAt))}</span>
      </p>
      {showThought ? <p className="agent-status__thought">{thought}</p> : null}
    </>
  );
}

/** Свёрнутый список шагов; раскрывается по нажатию. */
export function WorkLog({ steps, totalMs }: { steps: WorkStep[]; totalMs: number | null }) {
  const t = useT();
  if (steps.length === 0) return null;
  const summary = [stepsWord(steps.length), totalMs !== null ? seconds(totalMs) : null].filter(Boolean).join(' · ');
  return (
    <div className="agent-worklog">
      <Disclosure label={t(m.summaryLabel)} summary={summary}>
        <ol className="agent-worklog__list">
          {steps.map(({ part, ms }) => (
            <li
              key={part.toolCallId}
              className={`agent-worklog__step${part.state === 'output-error' ? ' agent-worklog__step--failed' : ''}`}
            >
              <span>{doneText(part)}</span>
              {ms !== null ? <span className="agent-worklog__time">{seconds(ms)}</span> : null}
            </li>
          ))}
        </ol>
      </Disclosure>
    </div>
  );
}
